import type { DailyScenario, GameState, Seat, Suit } from "./types";
import { isGregorianKey, londonDateKey, londonDayDistance } from "./calendar";
export { londonDateKey, londonDayDistance } from "./calendar";
interface Release { version: number; releasedThrough: string; scenarios: DailyScenario[] }
const CACHE_KEY = "trump-table:released-daily:v1";
let release: Release | null = null;
let pending: Promise<void> | null = null;
export function dailyReleaseThrough(): string | null { return release?.releasedThrough ?? null; }
export function acceptDailyRelease(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const next = value as Release;
  if (next.version !== 1 || typeof next.releasedThrough !== "string" || !isGregorianKey(next.releasedThrough) || !Array.isArray(next.scenarios) || next.scenarios.length > 3660) return false;
  const seen = new Set<string>();
  for (const scenario of next.scenarios) {
    if (!scenario || typeof scenario.publishDate !== "string" || !isGregorianKey(scenario.publishDate) || scenario.publishDate > next.releasedThrough || seen.has(scenario.publishDate) || scenario.id !== `tt-daily-${scenario.publishDate}` || typeof scenario.seed !== "string" || scenario.seed.length > 100 || !["order", "pass", "alone"].includes(scenario.kind) || !["order", "pass", "orderAlone"].includes(scenario.recommended) || !Number.isInteger(scenario.simulations) || scenario.simulations < 1 || !scenario.facts || !["clubs", "diamonds", "hearts", "spades"].includes(scenario.facts.upcardSuit) || !["9", "10", "J", "Q", "K", "A"].includes(scenario.facts.upcardRank) || ![scenario.facts.orderEV, scenario.facts.aloneEV, scenario.facts.passEV].every(Number.isFinite)) return false;
    seen.add(scenario.publishDate);
    const old = release?.scenarios.find(item => item.publishDate === scenario.publishDate);
    if (old && JSON.stringify(old) !== JSON.stringify(scenario)) return false;
  }
  if (release && (next.releasedThrough < release.releasedThrough || release.scenarios.some(scenario => !seen.has(scenario.publishDate)))) return false;
  release = structuredClone(next);
  return true;
}
export function restoreDailyRelease(): void {
  try { const cached = localStorage.getItem(CACHE_KEY); if (cached) acceptDailyRelease(JSON.parse(cached)); } catch { /* Local cache is optional. */ }
}
export function refreshDailyRelease(): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return Promise.resolve();
  // Published identities cannot change during their London day. A complete
  // current-day prefix needs no network; missing or next-day data still does.
  if (release?.releasedThrough === londonDateKey() && release.scenarios.some(scenario => scenario.publishDate === release?.releasedThrough)) return Promise.resolve();
  if (pending) return pending;
  pending = (async () => {
    try {
      const response = await fetch("/daily-release.json", { cache: "no-store", signal: AbortSignal.timeout(2500) });
      if (response.ok && acceptDailyRelease(await response.json())) {
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(release)); } catch { /* Storage denial does not block play. */ }
      }
    } catch { /* Cached released days only; never fabricate a missing deal. */ }
    finally { pending = null; }
  })();
  return pending;
}
export function scenarioForDate(dateKey: string): DailyScenario | null {
  if (!isGregorianKey(dateKey)) return null;
  const scenario = release?.scenarios.find(item => item.publishDate === dateKey);
  return scenario ? structuredClone(scenario) : null;
}

/** Unfinished Daily sessions stay resumable for this many London days. */
export const DAILY_RESTORE_WINDOW_DAYS = 30;

/**
 * A saved Daily session may only continue while its full identity — London
 * date key, scenario id and deal seed — still matches the preloaded schedule,
 * and an unfinished hand only inside its 30-London-day recovery window.
 * device-local-v1 sessions (no London calendar marker) are legacy and never
 * resumed under the London calendar.
 */
export function isDailySessionRestorable(state: GameState, todayKey: string): boolean {
  if (state.mode !== "daily") return false;
  if (state.dailyCalendar !== "london" || !state.dailyDate || !state.dailyScenarioId) return false;
  const scenario = scenarioForDate(state.dailyDate);
  if (!scenario || scenario.id !== state.dailyScenarioId || scenario.seed !== state.seed) return false;
  const elapsed = londonDayDistance(state.dailyDate, todayKey);
  if (!Number.isFinite(elapsed) || elapsed < 0) return false;
  if (state.status === "completed") return true;
  return elapsed <= DAILY_RESTORE_WINDOW_DAYS;
}

export interface DailyCallDecision {
  action: "order" | "orderAlone" | "call" | "callAlone" | "pass";
  suit?: Suit;
}

const HUMAN: Seat = 0;

/** The human's first calling decision of this hand, parsed from the history. */
export function dailyCallDecision(state: GameState): DailyCallDecision | null {
  for (const event of state.history) {
    const order = event.match(/^0:order:([a-z]+)(:alone)?$/);
    if (order) return { action: order[2] ? "orderAlone" : "order", suit: order[1] as Suit };
    const call = event.match(/^0:call:([a-z]+)(:alone)?$/);
    if (call) return { action: call[2] ? "callAlone" : "call", suit: call[1] as Suit };
    if (event === `${HUMAN}:pass`) return { action: "pass" };
    // A hand event means the human never spoke in the calling rounds.
    if (event.startsWith("hand:")) return null;
  }
  return null;
}

/**
 * Whether the human's first calling decision matched the scenario
 * recommendation. The comparison is about the round-one judgment the
 * scenario was built for: ordering up (with or without alone) versus passing.
 */
export function dailyCallMatched(state: GameState, scenario: DailyScenario): boolean {
  const decision = dailyCallDecision(state);
  if (!decision) return false;
  if (scenario.recommended === "order") return decision.action === "order";
  if (scenario.recommended === "orderAlone") return decision.action === "orderAlone";
  return decision.action === "pass";
}
